/**
 * Minimal Telemetry Utility
 * Tracks API calls and health status in localStorage
 */

const STATS_KEY = 'phlrent_telemetry';

/**
 * Get current stats object
 */
function getStats() {
  try {
    return JSON.parse(localStorage.getItem(STATS_KEY) || '{}');
  } catch {
    return {};
  }
}

/**
 * Save stats object
 */
function saveStats(stats) {
  try {
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  } catch (e) {
    console.warn('Telemetry save error:', e);
  }
}

/**
 * Get today's date key (YYYY-MM-DD)
 */
function getToday() {
  return new Date().toISOString().split('T')[0];
}

/**
 * Track an API call
 * @param {string} endpoint - API endpoint name
 * @param {boolean} success - Whether the call succeeded
 * @param {number} duration - Response time in ms
 */
export function trackApiCall(endpoint, success, duration = 0) {
  const stats = getStats();
  const today = getToday();

  // Initialize today's stats if needed
  if (!stats[today]) {
    stats[today] = { calls: 0, errors: 0, cacheHits: 0, totalDuration: 0 };
  }

  stats[today].calls++;
  if (!success) stats[today].errors++;
  stats[today].totalDuration += duration;

  // Track last call timestamp and status
  stats.lastCall = {
    timestamp: Date.now(),
    endpoint,
    success,
    duration
  };

  // Keep only last 30 days of data
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  const cutoffStr = cutoff.toISOString().split('T')[0];
  Object.keys(stats).forEach(key => {
    if (key < cutoffStr && key !== 'lastCall') {
      delete stats[key];
    }
  });

  saveStats(stats);
}

/**
 * Track a cache hit (no API call needed)
 */
export function trackCacheHit() {
  const stats = getStats();
  const today = getToday();

  if (!stats[today]) {
    stats[today] = { calls: 0, errors: 0, cacheHits: 0, totalDuration: 0 };
  }

  stats[today].cacheHits++;
  saveStats(stats);
}

/**
 * Get telemetry summary
 * @returns {Object} Summary stats
 */
export function getStats7Day() {
  const stats = getStats();
  const today = new Date();

  let totalCalls = 0;
  let totalErrors = 0;
  let totalCacheHits = 0;
  let totalDuration = 0;

  for (let i = 0; i < 7; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const key = date.toISOString().split('T')[0];

    if (stats[key]) {
      totalCalls += stats[key].calls || 0;
      totalErrors += stats[key].errors || 0;
      totalCacheHits += stats[key].cacheHits || 0;
      totalDuration += stats[key].totalDuration || 0;
    }
  }

  const avgDuration = totalCalls > 0 ? Math.round(totalDuration / totalCalls) : 0;
  const successRate = totalCalls > 0 ? Math.round(((totalCalls - totalErrors) / totalCalls) * 100) : 100;

  return {
    calls: totalCalls,
    errors: totalErrors,
    cacheHits: totalCacheHits,
    avgDuration,
    successRate,
    lastCall: stats.lastCall || null
  };
}

/**
 * Get health status
 * @returns {Object} Health status with color indicator
 */
export function getHealthStatus() {
  const stats = getStats7Day();

  // Determine health based on success rate and recent activity
  let status = 'healthy';
  let color = '#10b981'; // green

  if (stats.successRate < 90) {
    status = 'degraded';
    color = '#f59e0b'; // amber
  }

  if (stats.successRate < 50 || (stats.lastCall && !stats.lastCall.success)) {
    status = 'unhealthy';
    color = '#ef4444'; // red
  }

  // Check if last call was recent (within 24 hours)
  const lastCallAge = stats.lastCall
    ? Date.now() - stats.lastCall.timestamp
    : Infinity;
  const isStale = lastCallAge > 24 * 60 * 60 * 1000;

  return {
    status,
    color,
    isStale,
    ...stats
  };
}

/**
 * Log stats to console (for debugging)
 */
export function logStats() {
  const health = getHealthStatus();
  console.group('PHLRenting Telemetry');
  console.log(`Status: ${health.status.toUpperCase()}`);
  console.log(`7-day API calls: ${health.calls}`);
  console.log(`Cache hits: ${health.cacheHits}`);
  console.log(`Success rate: ${health.successRate}%`);
  console.log(`Avg response: ${health.avgDuration}ms`);
  if (health.lastCall) {
    console.log(`Last call: ${new Date(health.lastCall.timestamp).toLocaleString()}`);
  }
  console.groupEnd();
}

// Expose to window for console debugging
if (typeof window !== 'undefined') {
  window.phlrentStats = logStats;
}
